import { ILogEvent, DurationMeasureType } from "../interfaces/ILogEvent";
import { LogEventNames, ILogData, LogValueType } from "../interfaces/ITelemetryLogger";
import * as Logger from "./Details/Logger";
import { TELEMETRY_EVENT_AGGREGATION_TIME_INTERVAL_MILLISECONDS } from "../../AppConstants";
import { v4 as uuidv4 } from 'uuid';

/**
 * Interface to define the state of an ongoing duration measurement
 */
interface IRunningDuration {
    type: DurationMeasureType,
    starttime: number,
    value: number
};

/**
 * Describes the state last emitted by the LogEvent
 */
enum LogEventState {

    // Start event emitted
    Start = "Start",

    // End event emitted
    End = "End",

    // Start event was emitted, but End event wasn't
    Incomplete = "Incomplete"
};

/**
 * Default LogEvent keys.
 */
enum LogEventSharedKeys {
    EventID = 'EventID', // Included in every actual telemetry event we log, represents a unique identifier for this LogEvent instance.
    EventState = 'EventState' // This represents event state, as described in LogEventState
}

/**
 * Generic implementation for ILogEvent, to be used for any new event
 * 
 * Any new events should:
 * a) have an entry in LogEventNames to pass into the constructor
 * b) should define a set of "Keys" to be used as column headers for the event
 * c) add an entry to the list in LogEvents.ts in order to use it for logging purposes
 * 
 * KeyType should be a key type enum associated with the event name (that represents a unique column in the event)
 * This logs an event at Start and at End. 
 * It also logs an event every 60 seconds in between the Start and End, if anything has changed
 * 
 * This implementation is to aggregate/batch the logging into events at a pre-determined time period 
 * (60s for now - see TelemetryEventAggregationTimeIntervalMilliseconds)
 * We need to use this implementation to avoid littering events for every log statement 
 * (especially inside tight loops) and to be able to more easily query telemetry data to generate reports.
 * "Logger" is a lower level module, that is exposed only to provide a means for providing mocks for unit test purposes
 * so it's not meant to be used directly
 * 
 * While the Activity API offers something similar, from chatting with the OTel folks, it sounds like that API
 * isn't as completely supported/planned to be supported as the C++ version of the API. We also have additional
 * requirements (such as sending a start event, sending every 60 seconds, custom duration measures/etc) that
 * means we have avoided using that API for v1. (However, if we decided to use it, we could still simply switch
 * the implementations inside this class and the callsites still wouldn't require to change)
 * 
 */
export class LogEvent <T extends string> implements ILogEvent<T> {
    private eventName: LogEventNames = null;
    private payload: Map<T | LogEventSharedKeys, LogValueType> = null;
    private previousPayload: Map<T | LogEventSharedKeys, LogValueType> = null;
    private runningDurations: Map<T, IRunningDuration> = null;
    private runningCounters: Map<T, number> = null;
    private intervalID: number = 0;
    private eventID: string;

    constructor(eventName: LogEventNames) {
        this.eventName = eventName;
    }

    /**
     * Emits an event indicating the start of an aggregated event
     * If this event was previously already started and not ended, then it first attempts to end that event as "incomplete"
     * It will continuously emit an aggregated state of the event every 60 seconds, until the End event is called
     * 
     * @param additionalData Any additional data to be logged along with the Start event
     */
    Start(additionalData?: ILogData<T>[]) {
        if (!this.eventName) {
            return;
        }

        if (this.IsEventStarted()) {
            this.payload.set(LogEventSharedKeys.EventState, LogEventState.Incomplete);
            this.End();
        }

        this.eventID = uuidv4();
        this.payload = new Map<T | LogEventSharedKeys, LogValueType>();
        this.payload.set(LogEventSharedKeys.EventState, LogEventState.Start);
        if (additionalData) {
            additionalData.forEach((value: ILogData<T>) => {
                this.payload.set(value.key, value.value);
            });
        }

        this.LogState();
        this.intervalID = window.setInterval(() => this.LogState(), TELEMETRY_EVENT_AGGREGATION_TIME_INTERVAL_MILLISECONDS /*ms*/);
    }

    /**
     * Emits an event indicating the end of an aggregated event
     * If this event was previously already ended, it silently returns
     * It will also reset all internal state of the event so that it can be reused with a call to Start - if needed
     * 
     * @param additionalData Any additional data to be logged along with the End event
     */
    End(additionalData?: ILogData<T>[]): string {
        let stage: string = "called"
        if (!this.IsEventStarted()) {
            stage = "IsEventStarted failed";
            return stage;
        }

        stage = "after IsEventStarted";

        if (this.intervalID != 0) {
            stage = "intervalID not zero";
            window.clearInterval(this.intervalID);
            this.intervalID = 0;
        }

        stage = "after intervalID";

        // This assumes that the ordering of additionalData being logged after the key here ensures that
        // any "Incomplete" events (originating from the Start call above) are added
        // after the call to set it to "End". This ensures that telemetry emits it as "incomplete"
        this.payload.set(LogEventSharedKeys.EventState, LogEventState.End);
        stage = "after payload set";

        if (additionalData) {
            stage = "additionalData";
            additionalData.forEach((value: ILogData<T>) => {
                this.payload.set(value.key, value.value);
            });
        }

        stage = "after additionalData";
        const logstateState: string = this.LogState();
        stage = "after LogState. " + logstateState;

        if (this.runningDurations) {
            this.runningDurations.clear();
            this.runningDurations = null;
        }

        stage = "after runningDurations. " + logstateState;

        if (this.runningCounters) {
            this.runningCounters.clear();
            this.runningCounters = null;
        }

        stage = "after runningCounters. " + logstateState;

        if (this.previousPayload) {
            this.previousPayload.clear();
            this.previousPayload = null;
        }

        stage = "after previousPayload. " + logstateState;

        if (this.payload) {
            this.payload.clear();
            this.payload = null;
        }

        stage = "completed function. " + logstateState;

        return stage;
    }

    /**
     * Adds a data item to be emitted with the next aggregated event
     * 
     * @param data Defines a key, value pair to be added to the aggregated data, where Key is a column header in the aggregation and value is the value for it
     */
    AddData(data: ILogData<T>[]) {
        if (!this.IsEventStarted()) {
            return;
        }

        data.forEach((value: ILogData<T>) => {
            this.payload.set(value.key, value.value);
        });
    }

    /**
     * Begins the measurement of some duration
     * 
     * @param type Type of the duration to be measured. @see DurationMeasureType for details
     * @param key The column header to use for logging in the aggregated event
     */
    BeginDurationMeasure(type: DurationMeasureType, key: T) {
        if (!this.IsEventStarted()) {
            return;
        }

        if (!this.runningDurations) {
            this.runningDurations = new Map<T, IRunningDuration>();
        }

        let runningDuration: IRunningDuration | undefined = this.runningDurations.get(key);
        if (runningDuration == undefined) {
            runningDuration = {
                type,
                starttime: Date.now(),
                value: 0
            };
        } else if (runningDuration.starttime == 0){
            runningDuration.starttime = Date.now();
        }

        this.runningDurations.set(key, runningDuration);
    }

    /**
     * Ends the measurement of some duration and adds it to to be logged with the next aggregated event emission
     * 
     * @param key The column header to use for logging in the aggregated event
     */
    EndDurationMeasure(key: T) {
        if (!this.IsEventStarted()) {
            return;
        }

        if (!this.runningDurations) {
            return;
        }

        let runningDuration: IRunningDuration | undefined = this.runningDurations.get(key);
        if (runningDuration == undefined || runningDuration.starttime == 0) {
            return;
        }
    
        const durationSeconds: number = this.PerformEndDurationMeasure(key, runningDuration);
        switch(runningDuration.type) {
            case DurationMeasureType.Normal:
                this.runningDurations.delete(key);
                break;
            case DurationMeasureType.Accumulated:
                runningDuration.value = durationSeconds;
                runningDuration.starttime = 0;
                this.runningDurations.set(key, runningDuration);
                break;
            case DurationMeasureType.Slowest:
                if (durationSeconds > runningDuration.value) {
                    runningDuration.value = durationSeconds;
                }

                runningDuration.starttime = 0;
                this.runningDurations.set(key, runningDuration);
                break;
            default:
                break;
        }
    }

    /**
     * Increments the count associated with the key, which will subsequently be logged with the next aggregated event emission
     * 
     * @param key The column header to use for logging in the aggregated event
     */
    IncrementCount(key: T) {
        if (!this.IsEventStarted()) {
            return;
        }

        if (!this.runningCounters) {
            this.runningCounters = new Map<T, number>();
        }

        const count: number | undefined = this.runningCounters.get(key);
        if (count == undefined) {
            this.runningCounters.set(key, 1);
        } else {
            this.runningCounters.set(key, count + 1);
        }
    }

    /**
     * Returns the name of this event
     */
    GetEventName(): LogEventNames {
        return this.eventName;
    }

    protected IsEventStarted(): boolean {
        return (this.payload != null);
    }

    private PerformEndDurationMeasure(key: T, runningDuration: IRunningDuration): number {
        if (runningDuration.starttime == 0) {
            return 0;
        }
    
        let durationSeconds: number = (Date.now() - runningDuration.starttime) / 1000;
        switch(runningDuration.type) {
            case DurationMeasureType.Normal:
                this.payload.set(key, durationSeconds);
                break;
            case DurationMeasureType.Accumulated:
                durationSeconds += runningDuration.value;
                this.payload.set(key, durationSeconds);
                break;
            case DurationMeasureType.Slowest:
                if (durationSeconds > runningDuration.value) {
                    this.payload.set(key, durationSeconds);
                }
                break;
            default:
                break;
        }

        return durationSeconds;
    }

    private HasStateChanged(): boolean {
        if (this.payload.size == 0)
            return false;
        
        if (!this.previousPayload)
            return true;

        if (this.payload.size != this.previousPayload.size)
            return true;

        // we cannot use this.payload.forEach here because we'd like to return early in the case that we discover that the maps are different
        // whereas forEach uses a lambda - so returning there simply returns out of the lambda, and not out of our containing function.
        for (const [key, value] of this.payload) {
            const otherValue: LogValueType = this.previousPayload.get(key);
            if (otherValue !== value || (otherValue === undefined && !this.previousPayload.has(key))) {
                return true;
            }
        }

        return false;
    }

    /**
     * The function that emits the current state of this aggregated event - provided something changed from the previous call to this function
     * This will also emit the current state of any ongoing counters/duration measures
     */
    private LogState(): string {
        let state: string = "LogState called";
        if (!this.payload) {
            state = "no payload";
            return state;
        }

        state = "after payload check";

        this.payload.set(LogEventSharedKeys.EventID, this.eventID);

        state = "after payload set";

        if (this.runningDurations) {
            this.runningDurations.forEach((value: IRunningDuration, key: T) => {
                this.PerformEndDurationMeasure(key, value);
            });
        }

        state = "after runningDurations set";

        if (this.runningCounters) {
            this.runningCounters.forEach((value: number, key: T) => {
                this.payload.set(key, value);
            });
        }

        state = "after runningCounters set";

        if (this.HasStateChanged()) {
            state = "HasStateChanged is true";

            const data: ILogData<T | LogEventNames>[] = [];


            if (this.previousPayload) {
                this.previousPayload.clear();
            } else {
                this.previousPayload = new Map<T, LogValueType>();
            }

            this.payload.forEach((value: LogValueType, key: T) => { 
                data.push({key, value});
                this.previousPayload.set(key, value);
            });

            this.payload.clear();
            Logger.LogEvent({ eventName: this.eventName, data });

            state = "logger has logged";
        }

        return state;
    }
};